import React from 'react';
import AdminTenantSelector, { type AdminTenantOption } from '../components/AdminTenantSelector';
import type {
  RiskDashboardEntryDetailResponse,
  RiskDashboardFilters,
  RiskDashboardFindingDetailResponse,
  RiskDashboardFindingListResponse,
  RiskDashboardOverview,
  RiskDashboardTopFinding,
  RiskRegisterOrigin
} from '../contracts/riskSummaryContracts';
import { usePortalContext } from '../context/PortalContext';
import { CONCEALED_REPORT_NAME, loadReportNameConcealment } from '../services/reportNameConcealmentClient';

const defaultFilters: RiskDashboardFilters = {
  highRiskOnly: false,
  overdueOnly: false,
  staleAcceptedOnly: false,
  top: 100
};

const cardStyle: React.CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 12,
  padding: 16,
  background: '#fff'
};

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gap: 16
};

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const buildSourceUrl = (finding: RiskDashboardTopFinding | undefined): string | undefined => {
  if (!finding?.pageUrl && !finding?.siteUrl) {
    return undefined;
  }
  const base = finding.pageUrl
    ? new URL(finding.pageUrl, finding.siteUrl || window.location.origin)
    : new URL(finding.siteUrl!);
  // Deep-link params only exist for finding rows; a standalone entry's
  // source is the register tab on the instance page itself (§3.3a).
  if (finding.origin !== 'standalone' && finding.auditId && finding.findingId) {
    base.searchParams.set('auditId', String(finding.auditId));
    base.searchParams.set('findingId', String(finding.findingId));
  }
  return base.toString();
};

// Tracker #59 §3.3a union rows: origin-aware stable row key + display title.
const rowKey = (row: RiskDashboardTopFinding): string =>
  row.origin === 'standalone'
    ? `${row.instanceId}:standalone:${row.entryId}`
    : `${row.instanceId}:${row.auditId}:${row.findingId}`;
const rowTitle = (row: RiskDashboardTopFinding): string =>
  (row.origin === 'standalone' ? row.entryTitle : row.findingTitle) || 'Untitled';
const csvEscape = (value: string): string => `"${value.replace(/"/g, '""')}"`;

const formatDate = (value?: string): string => value ? new Date(value).toLocaleString() : '—';
const formatScore = (value?: number): string => typeof value === 'number' ? String(value) : '—';


interface EscalationRule {
  ruleId: string;
  name: string;
  conditionType: 'band_at_or_above_unaccepted' | 'acceptance_review_overdue' | 'mitigation_overdue_at_band';
  minNormalisedScore?: number;
  bandLabel?: string;
  graceDays: number;
  recipients: string[];
  enabled: boolean;
  source: 'spfx' | 'portal';
}

const CONDITION_LABELS: Record<EscalationRule['conditionType'], string> = {
  band_at_or_above_unaccepted: 'Band at/above threshold and not accepted',
  acceptance_review_overdue: 'Acceptance review date passed',
  mitigation_overdue_at_band: 'Overdue mitigation at/above threshold'
};

const emptyRuleDraft = (): Omit<EscalationRule, 'source'> => ({
  ruleId: '',
  name: '',
  conditionType: 'band_at_or_above_unaccepted',
  minNormalisedScore: 0.6,
  bandLabel: '',
  graceDays: 0,
  recipients: [],
  enabled: true
});

/**
 * Tracker #59 §3.4 (D9 RULED 2026-07-10 — configurable authoring
 * authority): a portal admin decides PER INSTANCE whether escalation
 * rules are managed at instance level (SPFx authors, portal read-only)
 * or tenant level (portal authors here, SPFx read-only).
 */
const EscalationRulesCard = ({ tenantId }: { tenantId: string }): JSX.Element => {
  const [instanceId, setInstanceId] = React.useState('');
  const [loadedInstanceId, setLoadedInstanceId] = React.useState('');
  const [authority, setAuthority] = React.useState<'instance' | 'tenant'>('instance');
  const [rules, setRules] = React.useState<EscalationRule[]>([]);
  const [draft, setDraft] = React.useState(emptyRuleDraft());
  const [recipientsText, setRecipientsText] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [message, setMessage] = React.useState<string | undefined>();

  const load = React.useCallback(async (targetInstanceId: string): Promise<void> => {
    if (!tenantId || !targetInstanceId.trim()) {
      return;
    }
    setBusy(true);
    setMessage(undefined);
    try {
      const params = new URLSearchParams({ tenantId, instanceId: targetInstanceId.trim() });
      const response = await fetch(`/api/admin/risk/escalation?${params.toString()}`, { credentials: 'same-origin' });
      if (!response.ok) {
        throw new Error(`Escalation config ${response.status}`);
      }
      const payload = (await response.json()) as { authority: 'instance' | 'tenant'; rules: EscalationRule[] };
      setAuthority(payload.authority);
      setRules(payload.rules || []);
      setLoadedInstanceId(targetInstanceId.trim());
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setBusy(false);
    }
  }, [tenantId]);

  const saveAuthority = React.useCallback(async (nextAuthority: 'instance' | 'tenant'): Promise<void> => {
    if (!loadedInstanceId) return;
    setBusy(true);
    setMessage(undefined);
    try {
      const response = await fetch('/api/admin/risk/escalation/authority', {
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ tenantId, instanceId: loadedInstanceId, authority: nextAuthority })
      });
      if (!response.ok) {
        throw new Error(`Authority ${response.status}`);
      }
      setAuthority(nextAuthority);
      setMessage(`Authoring authority set to ${nextAuthority === 'tenant' ? 'tenant (portal authors)' : 'instance (SPFx authors)'}.`);
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setBusy(false);
    }
  }, [tenantId, loadedInstanceId]);

  const saveRule = React.useCallback(async (): Promise<void> => {
    if (!loadedInstanceId) return;
    setBusy(true);
    setMessage(undefined);
    try {
      const recipients = recipientsText.split(/[;,\s]+/).map((value) => value.trim()).filter(Boolean);
      const response = await fetch('/api/admin/risk/escalation/rules', {
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          tenantId,
          instanceId: loadedInstanceId,
          rule: { ...draft, ruleId: draft.ruleId || `portal-${Date.now()}`, recipients }
        })
      });
      if (!response.ok) {
        const body = await response.json().catch(() => undefined) as { error?: { message?: string } } | undefined;
        throw new Error(body?.error?.message || `Rule save ${response.status}`);
      }
      setDraft(emptyRuleDraft());
      setRecipientsText('');
      await load(loadedInstanceId);
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setBusy(false);
    }
  }, [tenantId, loadedInstanceId, draft, recipientsText, load]);

  const deleteRule = React.useCallback(async (ruleId: string): Promise<void> => {
    if (!loadedInstanceId) return;
    setBusy(true);
    setMessage(undefined);
    try {
      const params = new URLSearchParams({ tenantId, instanceId: loadedInstanceId });
      const response = await fetch(`/api/admin/risk/escalation/rules/${encodeURIComponent(ruleId)}?${params.toString()}`, {
        method: 'DELETE',
        credentials: 'same-origin'
      });
      if (!response.ok) {
        throw new Error(`Rule delete ${response.status}`);
      }
      await load(loadedInstanceId);
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setBusy(false);
    }
  }, [tenantId, loadedInstanceId, load]);

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 8 }}>
        <strong>Auto-escalation rules</strong>
        <span className="muted">Hourly evaluation over the synced risk register; e-mails once per condition, re-fires on a re-crossing.</span>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'end', flexWrap: 'wrap' }}>
        <label>
          <div className="muted">Instance ID</div>
          <input className="input" value={instanceId} onChange={(e) => setInstanceId(e.target.value)} />
        </label>
        <button className="btn secondary" type="button" disabled={busy || !instanceId.trim()} onClick={() => { void load(instanceId); }}>
          Load configuration
        </button>
      </div>
      {message && <div className="pill warning" style={{ marginTop: 8 }}>{message}</div>}
      {loadedInstanceId && (
        <div style={{ marginTop: 12, display: 'grid', gap: 12 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="muted">Authoring authority (D9 — per instance):</span>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="radio" checked={authority === 'instance'} disabled={busy} onChange={() => { void saveAuthority('instance'); }} />
              <span>Instance (SPFx authors; portal read-only)</span>
            </label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="radio" checked={authority === 'tenant'} disabled={busy} onChange={() => { void saveAuthority('tenant'); }} />
              <span>Tenant (portal authors; SPFx read-only)</span>
            </label>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {['Name', 'Condition', 'Threshold', 'Grace days', 'Recipients', 'Enabled', 'Source', ''].map((label) => (
                    <th key={label} style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: '6px 8px' }}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => (
                  <tr key={rule.ruleId}>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>{rule.name}</td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>{CONDITION_LABELS[rule.conditionType]}</td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>
                      {typeof rule.minNormalisedScore === 'number' ? `${Math.round(rule.minNormalisedScore * 100)}%${rule.bandLabel ? ` (${rule.bandLabel})` : ''}` : '—'}
                    </td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>{rule.graceDays}</td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>{rule.recipients.join('; ')}</td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>{rule.enabled ? 'Yes' : 'No'}</td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>{rule.source}</td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>
                      {authority === 'tenant' && (
                        <button className="btn secondary" type="button" disabled={busy} onClick={() => { void deleteRule(rule.ruleId); }}>
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rules.length === 0 && <p className="muted">No escalation rules for this instance yet.</p>}
          </div>
          {authority === 'instance' ? (
            <p className="muted">Rules for this instance are authored in the SPFx risk admin surface; this view is read-only.</p>
          ) : (
            <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', alignItems: 'end' }}>
              <label>
                <div className="muted">Rule name</div>
                <input className="input" value={draft.name} onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))} />
              </label>
              <label>
                <div className="muted">Condition</div>
                <select className="input" value={draft.conditionType} onChange={(e) => setDraft((prev) => ({ ...prev, conditionType: e.target.value as EscalationRule['conditionType'] }))}>
                  <option value="band_at_or_above_unaccepted">{CONDITION_LABELS.band_at_or_above_unaccepted}</option>
                  <option value="acceptance_review_overdue">{CONDITION_LABELS.acceptance_review_overdue}</option>
                  <option value="mitigation_overdue_at_band">{CONDITION_LABELS.mitigation_overdue_at_band}</option>
                </select>
              </label>
              <label>
                <div className="muted">Threshold (% of framework maximum)</div>
                <input
                  className="input"
                  type="number"
                  min={0}
                  max={100}
                  value={typeof draft.minNormalisedScore === 'number' ? Math.round(draft.minNormalisedScore * 100) : ''}
                  onChange={(e) => setDraft((prev) => ({
                    ...prev,
                    minNormalisedScore: e.target.value === '' ? undefined : Math.min(100, Math.max(0, Number(e.target.value))) / 100
                  }))}
                />
              </label>
              <label>
                <div className="muted">Grace days (date-based conditions)</div>
                <input className="input" type="number" min={0} value={draft.graceDays} onChange={(e) => setDraft((prev) => ({ ...prev, graceDays: Math.max(0, Number(e.target.value) || 0) }))} />
              </label>
              <label style={{ gridColumn: 'span 2' }}>
                <div className="muted">Recipients (e-mail addresses, separated by ; )</div>
                <input className="input" value={recipientsText} onChange={(e) => setRecipientsText(e.target.value)} />
              </label>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft((prev) => ({ ...prev, enabled: e.target.checked }))} />
                <span>Enabled</span>
              </label>
              <div>
                <button className="btn" type="button" disabled={busy || !draft.name.trim() || !recipientsText.trim()} onClick={() => { void saveRule(); }}>
                  Save rule
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface SharedFrameworkMeta {
  instanceId: string;
  sourceFrameworkId: number;
  title: string;
  scale: number;
  scoringMode: 'multiplicative' | 'additive';
  publishedBy?: string;
  publishedAt: string;
}

/**
 * Tracker #59 §3.5 (D7 RULED 2026-07-10): shared-frameworks listing —
 * config metadata only (name, scale, mode, published by/when). Instances
 * publish from SPFx and import-as-copy from SPFx; the portal observes.
 */
const SharedFrameworksCard = ({ tenantId }: { tenantId: string }): JSX.Element => {
  const [frameworks, setFrameworks] = React.useState<SharedFrameworkMeta[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setErrorText] = React.useState<string | undefined>();

  React.useEffect(() => {
    if (!tenantId) {
      return;
    }
    let disposed = false;
    setLoading(true);
    setErrorText(undefined);
    const params = new URLSearchParams({ tenantId });
    fetch(`/api/admin/risk/shared-frameworks?${params.toString()}`, { credentials: 'same-origin' })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Shared frameworks ${response.status}`);
        }
        const payload = (await response.json()) as { frameworks?: SharedFrameworkMeta[] };
        if (!disposed) {
          setFrameworks(payload.frameworks ?? []);
        }
      })
      .catch((err) => {
        if (!disposed) {
          setErrorText((err as Error).message);
        }
      })
      .finally(() => {
        if (!disposed) {
          setLoading(false);
        }
      });
    return () => {
      disposed = true;
    };
  }, [tenantId]);

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 8 }}>
        <strong>Shared risk frameworks</strong>
        <span className="muted">Published tenant-wide from SPFx; other instances import a local copy (import-as-copy, D7).</span>
      </div>
      {error && <div className="pill warning">{error}</div>}
      {loading && <p className="muted">Loading…</p>}
      {!loading && frameworks.length === 0 && <p className="muted">No shared frameworks in this tenant yet.</p>}
      {!loading && frameworks.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                {['Name', 'Scale', 'Scoring mode', 'Published by', 'Published', 'Publishing instance'].map((label) => (
                  <th key={label} style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: '6px 8px' }}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {frameworks.map((framework) => (
                <tr key={`${framework.instanceId}:${framework.sourceFrameworkId}`}>
                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>{framework.title}</td>
                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>{framework.scale}×{framework.scale}</td>
                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>{framework.scoringMode}</td>
                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>{framework.publishedBy || '—'}</td>
                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>{formatDate(framework.publishedAt)}</td>
                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>{framework.instanceId}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const RiskReview = (): JSX.Element => {
  const { tenant } = usePortalContext();
  const canManageClients = tenant?.capabilities?.canUseRisk === true;
  const [deployableTenants, setDeployableTenants] = React.useState<AdminTenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = React.useState('');
  const [filters, setFilters] = React.useState<RiskDashboardFilters>(defaultFilters);
  const [overview, setOverview] = React.useState<RiskDashboardOverview | null>(null);
  const [findings, setFindings] = React.useState<RiskDashboardFindingListResponse | null>(null);
  const [detailRow, setDetailRow] = React.useState<RiskDashboardTopFinding | null>(null);
  const [selectedKey, setSelectedKey] = React.useState<string>('');
  const [loadingOverview, setLoadingOverview] = React.useState(false);
  const [loadingFindings, setLoadingFindings] = React.useState(false);
  const [loadingDetail, setLoadingDetail] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [concealNamesInReports, setConcealNamesInReports] = React.useState(false);
  const [privacyReady, setPrivacyReady] = React.useState(false);

  React.useEffect(() => {
    if (!selectedTenantId) {
      setPrivacyReady(false);
      return;
    }
    let cancelled = false;
    setPrivacyReady(false);
    loadReportNameConcealment(selectedTenantId)
      .then(conceal => {
        if (!cancelled) {
          setConcealNamesInReports(conceal);
          setPrivacyReady(true);
        }
      })
      .catch(() => {
        if (!cancelled) setPrivacyReady(false);
      });
    return () => { cancelled = true; };
  }, [selectedTenantId]);

  const selectedTenant = React.useMemo(
    () => deployableTenants.find((entry) => entry.tenantId === selectedTenantId) ?? null,
    [deployableTenants, selectedTenantId]
  );

  const loadTenants = React.useCallback(async (): Promise<void> => {
    try {
      const response = await fetch('/api/admin/spfx-releases/deployable-tenants', { credentials: 'same-origin' });
      if (!response.ok) {
        throw new Error(`Tenants ${response.status}`);
      }
      const payload = (await response.json()) as {
        tenants?: AdminTenantOption[];
        defaultTenantId?: string;
      };
      const tenants = Array.isArray(payload.tenants) ? payload.tenants : [];
      setDeployableTenants(tenants);
      setSelectedTenantId((current) => current || payload.defaultTenantId || tenants[0]?.tenantId || '');
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  const loadOverview = React.useCallback(async (): Promise<void> => {
    if (!selectedTenantId) {
      return;
    }
    setLoadingOverview(true);
    setError(undefined);
    try {
      const params = new URLSearchParams({
        top: '8',
        tenantId: selectedTenantId
      });
      const response = await fetch(`/api/admin/risk/overview?${params.toString()}`, { credentials: 'same-origin' });
      if (!response.ok) {
        throw new Error(`Overview ${response.status}`);
      }
      setOverview((await response.json()) as RiskDashboardOverview);
    } catch (err) {
      setError((err as Error).message);
      setOverview(null);
    } finally {
      setLoadingOverview(false);
    }
  }, [selectedTenantId]);

  const loadFindings = React.useCallback(async (nextFilters: RiskDashboardFilters): Promise<void> => {
    if (!selectedTenantId) {
      return;
    }
    setLoadingFindings(true);
    setError(undefined);
    try {
      const params = new URLSearchParams();
      params.set('tenantId', selectedTenantId);
      if (nextFilters.instanceId) params.set('instanceId', nextFilters.instanceId);
      if (typeof nextFilters.auditId === 'number') params.set('auditId', String(nextFilters.auditId));
      if (nextFilters.category) params.set('category', nextFilters.category);
      if (typeof nextFilters.frameworkId === 'number') params.set('frameworkId', String(nextFilters.frameworkId));
      if (nextFilters.accepted) params.set('accepted', nextFilters.accepted);
      if (nextFilters.highRiskOnly) params.set('highRiskOnly', 'true');
      if (nextFilters.overdueOnly) params.set('overdueOnly', 'true');
      if (nextFilters.staleAcceptedOnly) params.set('staleAcceptedOnly', 'true');
      if (nextFilters.origin) params.set('origin', nextFilters.origin);
      params.set('top', String(nextFilters.top || 100));
      const response = await fetch(`/api/admin/risk/findings?${params.toString()}`, { credentials: 'same-origin' });
      if (!response.ok) {
        throw new Error(`Findings ${response.status}`);
      }
      const json = (await response.json()) as RiskDashboardFindingListResponse;
      setFindings(json);
      setSelectedKey((prev) => {
        const first = json.findings[0];
        if (prev && json.findings.some((finding) => rowKey(finding) === prev)) {
          return prev;
        }
        return first ? rowKey(first) : '';
      });
    } catch (err) {
      setError((err as Error).message);
      setFindings(null);
      setSelectedKey('');
    } finally {
      setLoadingFindings(false);
    }
  }, [selectedTenantId]);

  const loadDetail = React.useCallback(async (row: RiskDashboardTopFinding | undefined): Promise<void> => {
    if (!row) {
      setDetailRow(null);
      return;
    }
    setLoadingDetail(true);
    setError(undefined);
    try {
      const params = new URLSearchParams({ tenantId: selectedTenantId });
      // §3.3a: the finding detail route stays finding-only; standalone
      // entries drill in through the sibling entries route.
      const path = row.origin === 'standalone'
        ? `/api/admin/risk/entries/${encodeURIComponent(row.instanceId)}/${encodeURIComponent(String(row.entryId))}`
        : `/api/admin/risk/findings/${encodeURIComponent(row.instanceId)}/${encodeURIComponent(String(row.auditId))}/${encodeURIComponent(String(row.findingId))}`;
      const response = await fetch(`${path}?${params.toString()}`, { credentials: 'same-origin' });
      if (!response.ok) {
        throw new Error(`Detail ${response.status}`);
      }
      if (row.origin === 'standalone') {
        const payload = (await response.json()) as RiskDashboardEntryDetailResponse;
        setDetailRow(payload.entry ?? null);
      } else {
        const payload = (await response.json()) as RiskDashboardFindingDetailResponse;
        setDetailRow(payload.finding ?? null);
      }
    } catch (err) {
      setError((err as Error).message);
      setDetailRow(null);
    } finally {
      setLoadingDetail(false);
    }
  }, [selectedTenantId]);

  React.useEffect(() => {
    if (!canManageClients) {
      return;
    }
    void loadTenants();
  }, [canManageClients, loadTenants]);

  React.useEffect(() => {
    if (!canManageClients || !selectedTenantId) {
      return;
    }
    void loadOverview();
  }, [canManageClients, loadOverview, selectedTenantId]);

  React.useEffect(() => {
    if (!canManageClients || !selectedTenantId) {
      return;
    }
    void loadFindings(filters);
  }, [canManageClients, filters, loadFindings, selectedTenantId]);

  React.useEffect(() => {
    const selected = findings?.findings.find((finding) => rowKey(finding) === selectedKey);
    void loadDetail(selected);
  }, [findings, selectedKey, loadDetail]);

  const selectedFinding = detailRow || findings?.findings.find((finding) => rowKey(finding) === selectedKey);

  const handleExport = React.useCallback((): void => {
    if (!overview || !findings) {
      return;
    }
    const reportWindow = window.open('', '_blank', 'width=1200,height=900');
    if (!reportWindow) {
      return;
    }
    const rows = findings.findings.map((finding) => `
      <tr>
        <td>${finding.origin === 'standalone' ? 'Standalone' : 'Finding'}</td>
        <td>${escapeHtml(finding.auditTitle || '')}</td>
        <td>${escapeHtml(rowTitle(finding))}</td>
        <td>${escapeHtml(finding.riskCategory || '')}</td>
        <td>${escapeHtml(finding.riskFrameworkTitle || '')}</td>
        <td>${escapeHtml(finding.residualRating || finding.initialRating || 'Unrated')}</td>
        <td>${escapeHtml(formatScore(finding.currentScore))}</td>
        <td>${finding.riskAccepted ? 'Accepted' : 'Pending'}</td>
        <td>${finding.isStaleAcceptance ? 'Stale' : ''}</td>
        <td>${finding.hasOverdueMitigation ? 'Yes' : 'No'}</td>
        <td>${escapeHtml(concealNamesInReports && finding.entryOwner ? CONCEALED_REPORT_NAME : finding.entryOwner || '')}</td>
        <td>${escapeHtml(finding.riskAcceptanceReviewDate || finding.entryReviewDate ? formatDate(finding.riskAcceptanceReviewDate || finding.entryReviewDate) : '')}</td>
      </tr>
    `).join('');
    reportWindow.document.write(`<!doctype html>
      <html>
        <head>
          <title>Tenant Risk Summary</title>
          <style>
            body { font-family: Segoe UI, sans-serif; color: #111827; margin: 24px; font-size: 12px; }
            h1, h2 { margin: 0 0 8px; }
            .meta, .totals { margin-bottom: 16px; }
            .totals span { display: inline-block; margin: 0 12px 8px 0; padding: 6px 10px; background: #f3f4f6; border-radius: 999px; }
            table { width: 100%; border-collapse: collapse; margin-top: 16px; }
            th, td { border: 1px solid #d1d5db; padding: 8px; text-align: left; vertical-align: top; }
            th { background: #f9fafb; }
          </style>
        </head>
        <body>
          <h1>Tenant Risk Summary</h1>
          <div class="meta">
            <div><strong>Produced on:</strong> ${escapeHtml(new Date().toLocaleString())}</div>
            <div><strong>Tenant:</strong> ${escapeHtml(selectedTenant?.displayName || selectedTenant?.sharePointHost || tenant?.tenantName || selectedTenantId)}</div>
            <div><strong>Produced by:</strong> ${escapeHtml(concealNamesInReports && tenant?.userEmail ? CONCEALED_REPORT_NAME : tenant?.userEmail || '')}</div>
          </div>
          <div class="totals">
            <span>Instances: ${overview.totals.reportingInstances}</span>
            <span>Audits: ${overview.totals.reportingAudits}</span>
            <span>Findings with risk: ${overview.totals.findingsWithRisk}</span>
            <span>High risk: ${overview.totals.highRiskFindings}</span>
            <span>Accepted high risk: ${overview.totals.acceptedHighRiskFindings}</span>
            <span>Overdue mitigations: ${overview.totals.overdueMitigations}</span>
          </div>
          <h2>Filtered register rows</h2>
          <table>
            <thead>
              <tr>
                <th>Origin</th>
                <th>Audit</th>
                <th>Title</th>
                <th>Category</th>
                <th>Framework</th>
                <th>Current rating</th>
                <th>Current score</th>
                <th>Acceptance</th>
                <th>Stale</th>
                <th>Overdue mitigation</th>
                <th>Owner</th>
                <th>Review date</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </body>
      </html>`);
    reportWindow.document.close();
    reportWindow.focus();
    window.setTimeout(() => reportWindow.print(), 150);
  }, [concealNamesInReports, findings, overview, tenant?.tenantName, tenant?.userEmail]);

  // Tracker #59 §3.3: register CSV export over the union rows.
  const handleExportCsv = React.useCallback((): void => {
    if (!findings || findings.findings.length === 0) {
      return;
    }
    const header = ['Origin', 'Ref', 'Title', 'Audit', 'Category', 'Framework', 'Current rating', 'Current score', 'Acceptance', 'Stale acceptance', 'Overdue mitigation', 'Owner', 'Review date'];
    const lines = findings.findings.map((row) => [
      row.origin === 'standalone' ? 'Standalone' : 'Finding',
      row.entryRef || '',
      rowTitle(row),
      row.auditTitle || '',
      row.riskCategory || '',
      row.riskFrameworkTitle || '',
      row.residualRating || row.initialRating || '',
      formatScore(row.currentScore),
      row.riskAccepted ? 'Accepted' : 'Pending',
      row.isStaleAcceptance ? 'Stale' : '',
      row.hasOverdueMitigation ? 'Yes' : 'No',
      concealNamesInReports && row.entryOwner ? CONCEALED_REPORT_NAME : row.entryOwner || '',
      row.riskAcceptanceReviewDate || row.entryReviewDate
        ? formatDate(row.riskAcceptanceReviewDate || row.entryReviewDate)
        : ''
    ].map(csvEscape).join(','));
    const csv = [header.map(csvEscape).join(','), ...lines].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'risk-register.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  }, [concealNamesInReports, findings]);

  if (!canManageClients) {
    return <div className="card">You do not have access to hosted risk review.</div>;
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div>
        <h1 className="section-title">Risk Review</h1>
        <p className="muted">
          Hosted, read-only cross-instance risk review. Editing remains in the source SPFx experience.
        </p>
      </div>

      <div style={cardStyle}>
        <AdminTenantSelector
          label="Tenant"
          value={selectedTenantId}
          options={deployableTenants}
          onChange={setSelectedTenantId}
          helperText="Risk review is tenant-scoped. Use the selector to switch between covered tenant environments."
        />
      </div>

      {error && <div className="pill warning">{error}</div>}

      <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
        <div style={cardStyle}><strong>Instances</strong><div>{loadingOverview ? '…' : overview?.totals.reportingInstances ?? '—'}</div></div>
        <div style={cardStyle}><strong>High risk</strong><div>{loadingOverview ? '…' : overview?.totals.highRiskFindings ?? '—'}</div></div>
        <div style={cardStyle}><strong>Accepted high risk</strong><div>{loadingOverview ? '…' : overview?.totals.acceptedHighRiskFindings ?? '—'}</div></div>
        <div style={cardStyle}><strong>Overdue mitigations</strong><div>{loadingOverview ? '…' : overview?.totals.overdueMitigations ?? '—'}</div></div>
        {/* Tracker #59 §3.1 — checklist risk tile: audit-level weighted fail scores synced under the D10-approved payload extension. */}
        <div style={cardStyle}>
          <strong>Checklist risk</strong>
          <div>
            {loadingOverview
              ? '…'
              : overview?.checklistRisk
                ? `avg ${overview.checklistRisk.averageWeightedScore}% / worst ${overview.checklistRisk.worstWeightedScore}%`
                : '—'}
          </div>
          {!loadingOverview && overview?.checklistRisk && (
            <div className="muted">{overview.checklistRisk.reportingAudits} audit{overview.checklistRisk.reportingAudits === 1 ? '' : 's'} reporting</div>
          )}
        </div>
      </div>

      {/* Tracker #59 §3.1 — checklist risk domain distribution strip (weighted fail over each domain's OWN weighted total). */}
      {!loadingOverview && overview?.checklistRisk && overview.checklistRisk.domains.length > 0 && (
        <div style={cardStyle}>
          <strong>Checklist risk by domain</strong>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            {overview.checklistRisk.domains.map((domain) => {
              const domainScore = domain.weightedTotalScore > 0
                ? Math.round((domain.weightedFailScore / domain.weightedTotalScore) * 100)
                : 0;
              return (
                <span key={domain.domain} className="badge-subtle">
                  {domain.domain}: {domainScore}% weighted fail ({domain.failCount}/{domain.totalCount} checks, {domain.auditCount} audit{domain.auditCount === 1 ? '' : 's'})
                </span>
              );
            })}
          </div>
        </div>
      )}

      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 12 }}>
          <strong>Filters</strong>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn secondary" type="button" onClick={() => setFilters(defaultFilters)}>Clear filters</button>
            <button className="btn secondary" type="button" onClick={handleExportCsv} disabled={!privacyReady || !findings || findings.findings.length === 0}>Export CSV</button>
            <button className="btn" type="button" onClick={handleExport} disabled={!privacyReady || !findings || findings.findings.length === 0}>Export PDF</button>
          </div>
        </div>
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
          <label>
            <div className="muted">Instance</div>
            <input className="input" value={filters.instanceId || ''} onChange={(e) => setFilters((prev) => ({ ...prev, instanceId: e.target.value || undefined }))} />
          </label>
          <label>
            <div className="muted">Audit ID</div>
            <input className="input" type="number" value={filters.auditId ?? ''} onChange={(e) => setFilters((prev) => ({ ...prev, auditId: e.target.value ? Number(e.target.value) : undefined }))} />
          </label>
          <label>
            <div className="muted">Category</div>
            <input className="input" value={filters.category || ''} onChange={(e) => setFilters((prev) => ({ ...prev, category: e.target.value || undefined }))} />
          </label>
          <label>
            <div className="muted">Framework ID</div>
            <input className="input" type="number" value={filters.frameworkId ?? ''} onChange={(e) => setFilters((prev) => ({ ...prev, frameworkId: e.target.value ? Number(e.target.value) : undefined }))} />
          </label>
          <label>
            <div className="muted">Acceptance</div>
            <select className="input" value={filters.accepted || ''} onChange={(e) => setFilters((prev) => ({ ...prev, accepted: (e.target.value || undefined) as 'accepted' | 'unaccepted' | undefined }))}>
              <option value="">All</option>
              <option value="accepted">Accepted</option>
              <option value="unaccepted">Unaccepted</option>
            </select>
          </label>
          <label>
            <div className="muted">Origin</div>
            <select className="input" value={filters.origin || ''} onChange={(e) => setFilters((prev) => ({ ...prev, origin: (e.target.value || undefined) as RiskRegisterOrigin | undefined }))}>
              <option value="">All (union)</option>
              <option value="finding">Finding-derived</option>
              <option value="standalone">Standalone</option>
            </select>
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 22 }}>
            <input type="checkbox" checked={Boolean(filters.highRiskOnly)} onChange={(e) => setFilters((prev) => ({ ...prev, highRiskOnly: e.target.checked }))} />
            <span>High risk only</span>
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 22 }}>
            <input type="checkbox" checked={Boolean(filters.overdueOnly)} onChange={(e) => setFilters((prev) => ({ ...prev, overdueOnly: e.target.checked }))} />
            <span>Overdue mitigations</span>
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 22 }}>
            <input type="checkbox" checked={Boolean(filters.staleAcceptedOnly)} onChange={(e) => setFilters((prev) => ({ ...prev, staleAcceptedOnly: e.target.checked }))} />
            <span>Stale accepted only</span>
          </label>
        </div>
        {/* Tracker #59 §3.3: the exception strip — the working design's named exception views as one-click filter presets. */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12, alignItems: 'center' }}>
          <span className="muted">Exceptions:</span>
          <button
            className="btn secondary"
            type="button"
            onClick={() => setFilters((prev) => ({ ...prev, highRiskOnly: true, accepted: 'unaccepted', overdueOnly: false, staleAcceptedOnly: false }))}
          >
            High risk not accepted
          </button>
          <button
            className="btn secondary"
            type="button"
            onClick={() => setFilters((prev) => ({ ...prev, highRiskOnly: false, accepted: undefined, overdueOnly: false, staleAcceptedOnly: true }))}
          >
            Stale acceptance
          </button>
          <button
            className="btn secondary"
            type="button"
            onClick={() => setFilters((prev) => ({ ...prev, highRiskOnly: false, accepted: undefined, overdueOnly: true, staleAcceptedOnly: false }))}
          >
            Overdue mitigation
          </button>
        </div>
      </div>

      <div style={{ ...gridStyle, gridTemplateColumns: '1.2fr 0.8fr' }}>
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <strong>Register</strong>
            <span className="muted">{loadingFindings ? 'Loading…' : `${findings?.filteredCount ?? 0} matched`}</span>
          </div>
          {/* Tracker #59 §3.3: register-shaped column layout over the union of finding-derived rows and standalone entries. */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {['Origin', 'Ref', 'Title', 'Audit', 'Category', 'Rating', 'Score', 'Accepted', 'Owner', 'Review'].map((label) => (
                    <th key={label} style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: '6px 8px' }}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(findings?.findings || []).map((row) => {
                  const key = rowKey(row);
                  const selected = key === selectedKey;
                  const review = row.riskAcceptanceReviewDate || row.entryReviewDate;
                  return (
                    <tr
                      key={key}
                      onClick={() => setSelectedKey(key)}
                      style={{ cursor: 'pointer', background: selected ? '#f5f9ff' : undefined }}
                    >
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>
                        {row.origin === 'standalone' ? 'Standalone' : 'Finding'}
                      </td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>{row.entryRef || ''}</td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>
                        <strong>{rowTitle(row)}</strong>
                        {row.isStaleAcceptance && <span className="pill warning" style={{ marginLeft: 6 }}>Stale</span>}
                        {row.hasOverdueMitigation && <span className="pill warning" style={{ marginLeft: 6 }}>Overdue</span>}
                      </td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>{row.auditTitle || ''}</td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>{row.riskCategory || ''}</td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>{row.residualRating || row.initialRating || 'Unrated'}</td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>{formatScore(row.currentScore)}</td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>{row.riskAccepted ? 'Accepted' : 'Pending'}</td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>{row.entryOwner || ''}</td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>{review ? formatDate(review) : ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!loadingFindings && (!findings || findings.findings.length === 0) && (
              <p className="muted">No register rows match the current filters.</p>
            )}
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <strong>Hosted drill-in</strong>
            {selectedFinding && buildSourceUrl(selectedFinding) && (
              <a className="cta secondary" href={buildSourceUrl(selectedFinding)} target="_blank" rel="noreferrer">
                Open source
              </a>
            )}
          </div>
          {loadingDetail && <p className="muted">Loading hosted detail…</p>}
          {!loadingDetail && selectedFinding && (
            <div style={{ display: 'grid', gap: 10 }}>
              <div>
                <h3 style={{ margin: 0 }}>{rowTitle(selectedFinding)}</h3>
                <div className="muted">
                  {selectedFinding.origin === 'standalone'
                    ? `Standalone entry${selectedFinding.entryRef ? ` • ${selectedFinding.entryRef}` : ''}${selectedFinding.entryOwner ? ` • ${selectedFinding.entryOwner}` : ''}`
                    : selectedFinding.auditTitle}
                </div>
                {selectedFinding.origin === 'standalone' && selectedFinding.entryReviewDate && (
                  <div className="muted">Entry review date: {formatDate(selectedFinding.entryReviewDate)}</div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <span className="badge-subtle">Framework: {selectedFinding.riskFrameworkTitle || 'Unspecified'}</span>
                <span className="badge-subtle">Current rating: {selectedFinding.residualRating || selectedFinding.initialRating || 'Unrated'}</span>
                <span className="badge-subtle">Current score: {formatScore(selectedFinding.currentScore)}</span>
              </div>
              <div>
                <strong>Initial risk</strong>
                <div className="muted">
                  Likelihood {formatScore(selectedFinding.initialLikelihood)} × Impact {formatScore(selectedFinding.initialImpact)} = {formatScore(selectedFinding.initialScore)}
                </div>
              </div>
              <div>
                <strong>Residual risk</strong>
                <div className="muted">
                  Likelihood {formatScore(selectedFinding.residualLikelihood)} × Impact {formatScore(selectedFinding.residualImpact)} = {formatScore(selectedFinding.residualScore)}
                </div>
              </div>
              <div>
                <strong>Mitigation progress</strong>
                <div className="muted">
                  {selectedFinding.mitigationActionsCompleted}/{selectedFinding.mitigationActionCount} complete
                  {selectedFinding.hasOverdueMitigation ? ' • overdue mitigation present' : ''}
                </div>
              </div>
              <div>
                <strong>Acceptance</strong>
                <div className="muted">
                  {selectedFinding.riskAccepted ? 'Accepted' : 'Pending'}
                  {selectedFinding.riskAcceptedBy ? ` • by ${selectedFinding.riskAcceptedBy}` : ''}
                  {selectedFinding.riskAcceptedAt ? ` • ${formatDate(selectedFinding.riskAcceptedAt)}` : ''}
                </div>
                {selectedFinding.riskAcceptanceReviewDate && (
                  <div className="muted">
                    Review date: {formatDate(selectedFinding.riskAcceptanceReviewDate)}
                    {selectedFinding.isStaleAcceptance ? ' • stale' : ''}
                  </div>
                )}
                {selectedFinding.riskAcceptanceRationale && (
                  <div className="muted" style={{ marginTop: 4 }}>
                    Rationale: {selectedFinding.riskAcceptanceRationale}
                  </div>
                )}
              </div>
              <div className="muted">Last synced: {formatDate(selectedFinding.syncedAt)}</div>
            </div>
          )}
          {!loadingDetail && !selectedFinding && (
            <p className="muted">Select a register row to inspect the read-only drill-in.</p>
          )}
        </div>
      </div>

      {/* Tracker #59 §3.4 (D9): per-instance escalation authoring authority + rules */}
      <EscalationRulesCard tenantId={selectedTenantId} />

      {/* Tracker #59 §3.5 (D7): tenant shared-frameworks listing — config metadata only */}
      <SharedFrameworksCard tenantId={selectedTenantId} />
    </div>
  );
};

export default RiskReview;


